import { useEffect } from 'react';
import { Button, Input, InputNumber, List, Space, Typography, Slider, Switch, Tooltip, Modal, message, Select } from 'antd';
import { PlusOutlined, DeleteOutlined, PlayCircleOutlined, PauseCircleOutlined, StopOutlined, EyeOutlined, UserOutlined, ReloadOutlined, ThunderboltOutlined, CameraOutlined, AimOutlined, VideoCameraAddOutlined } from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import { useRoamStore } from '../../stores/roamStore';
import { cameraControlsRegistry } from '../../stores/cameraControlsStore';
import { requestCapturePathFrame } from './PathCameraRig';
import { mirrorWaypointsFromCameras } from '../../utils/cameraPathMirror';

const { Text } = Typography;

// 左侧树"漫游"面板：漫游模式控制（轨道/第一人称互斥 + 飞行 + 复位）
// + 轨道相机（路径相机）列表/关键帧编辑/播放
// + 第一人称自动浏览路线部署（选点 → 演示/发布页自动游览）
export default function CameraPathPanel() {
  const { waypoints, cameraPathPlaying, cameraPathSpeed, addWaypoint, removeWaypoint, updateWaypoint, setCameraPathPlaying, setCameraPathSpeed, browseRoute, setBrowseRoute, addBrowsePoint, removeBrowsePoint } = useSceneStore();
  const { mode, setMode, isFlyMode, toggleFlyMode, resetRoamView } = useRoamStore();
  // 2026-10-16（P1-3 第一人称属性完善）：移动速度/视点高度/鼠标灵敏度
  const baseSpeed = useRoamStore((s) => s.baseSpeed);
  const setBaseSpeed = useRoamStore((s) => s.setBaseSpeed);
  const eyeHeight = useRoamStore((s) => s.eyeHeight);
  const setEyeHeight = useRoamStore((s) => s.setEyeHeight);
  const mouseSensitivity = useRoamStore((s) => s.mouseSensitivity);
  const setMouseSensitivity = useRoamStore((s) => s.setMouseSensitivity);

  // ===== 轨道相机（路径相机） =====
  const pathCameras = useSceneStore((s) => s.pathCameras);
  const addPathCamera = useSceneStore((s) => s.addPathCamera);
  const removePathCamera = useSceneStore((s) => s.removePathCamera);
  const updatePathCamera = useSceneStore((s) => s.updatePathCamera);
  const addKeyFrame = useSceneStore((s) => s.addKeyFrame);
  const updateKeyFrame = useSceneStore((s) => s.updateKeyFrame);
  const removeKeyFrame = useSceneStore((s) => s.removeKeyFrame);

  const activePathCameraId = useRoamStore((s) => s.activePathCameraId);
  const setActivePathCamera = useRoamStore((s) => s.setActivePathCamera);
  const selectedKeyFrameIndex = useRoamStore((s) => s.selectedKeyFrameIndex);
  const setSelectedKeyFrame = useRoamStore((s) => s.setSelectedKeyFrame);
  const isPathPlaying = useRoamStore((s) => s.isPathPlaying);
  const setPathPlaying = useRoamStore((s) => s.setPathPlaying);
  const playTime = useRoamStore((s) => s.playTime);
  const setPlayTime = useRoamStore((s) => s.setPlayTime);
  const showPathHelpers = useRoamStore((s) => s.showPathHelpers);
  const toggleShowPathHelpers = useRoamStore((s) => s.toggleShowPathHelpers);

  const activeCamera = pathCameras.find((c) => c.id === activePathCameraId) ?? null;
  const entitySelected = useRoamStore((s) => s.pathCameraEntitySelected);

  // 2026-10-13（用户反馈 18）："相机路径"段数 = 相机总和 - 1（≥2 台轨道相机时自动同步）
  // 2026-10-14（用户反馈：点漫游后镜头只落到第一个相机、不移动）：视点改为**镜像轨道相机**——
  //   N 台相机 = N 个视点（N-1 段，段时长=各视点 duration），位置/朝向取各相机最后一帧；
  //   此前自动同步的是占位视点 [10,10,10]（与相机无关）→ 播放时镜头停在占位点原地不动。
  const pathSegCount = Math.max(0, pathCameras.length - 1);
  const cameraPosesSig = pathCameras
    .map((c) => {
      const f = c.keyFrames[c.keyFrames.length - 1];
      return f ? `${f.position.join(',')}|${f.rotation.join(',')}` : '';
    })
    .join(';');
  useEffect(() => {
    if (pathCameras.length < 2) return; // 0/1 台时不干预（保留独立"视点漫游"用法）
    const st = useSceneStore.getState();
    // 1) 视点数 = 相机数（段数 = 相机数 - 1，与面板标注一致）
    const current = st.waypoints;
    if (current.length > pathCameras.length) {
      current.slice(pathCameras.length).forEach((w) => st.removeWaypoint(w.id));
    } else if (current.length < pathCameras.length) {
      for (let i = current.length; i < pathCameras.length; i++) {
        st.addWaypoint({ position: [0, 0, 0], target: [0, 0, 0], duration: 3 });
      }
    }
    // 2) 位置/朝向镜像各相机最后一帧（保留用户已调整的 duration）
    const mirrored = mirrorWaypointsFromCameras(pathCameras);
    const latest = useSceneStore.getState().waypoints;
    mirrored.forEach((m, i) => {
      const wp = latest[i];
      if (!wp) return;
      st.updateWaypoint(wp.id, { position: m.position, target: m.target });
    });
  }, [cameraPosesSig, pathCameras.length]);

  /** 生成确认添加提示内容：显示当前场景相机位姿 */
  const buildConfirmContent = () => {
    const cam = cameraControlsRegistry.getCamera?.();
    if (!cam) return '将当前视角（场景相机位姿）加入路径相机';
    const fmt = (v: number) => v.toFixed(1);
    return (
      <div>
        <div>将当前场景相机位姿加入路径相机：</div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c', lineHeight: 1.8 }}>
          <div>位置 (X, Y, Z)：({fmt(cam.position[0])}, {fmt(cam.position[1])}, {fmt(cam.position[2])})</div>
          <div>目标 (X, Y, Z)：({fmt(cam.target[0])}, {fmt(cam.target[1])}, {fmt(cam.target[2])})</div>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#faad14' }}>
          确认后关键帧将同步显示在下方关键帧列表，可继续在场景中移动相机后再次添加。
        </div>
      </div>
    );
  };

  const confirmAddPathCamera = () => {
    Modal.confirm({
      title: '添加路径相机',
      content: buildConfirmContent(),
      okText: '确认添加',
      cancelText: '取消',
      onOk: () => {
        // 先创建空路径相机，再立刻捕获当前视角为第一帧
        const id = addPathCamera({
          name: `路径相机 ${pathCameras.length + 1}`,
          keyFrames: [],
          loop: true,
          interpolation: 'catmullRom',
        });
        setActivePathCamera(id);
        requestCapturePathFrame();
      },
    });
  };

  const confirmAddKeyFrame = () => {
    if (!activeCamera) return;
    Modal.confirm({
      title: '确认添加关键帧',
      content: buildConfirmContent(),
      okText: '确认添加',
      cancelText: '取消',
      onOk: () => requestCapturePathFrame(),
    });
  };

  const activeDuration = activeCamera && activeCamera.keyFrames.length > 0
    ? Math.max(...activeCamera.keyFrames.map((f) => f.time))
    : 0;

  return (
    <div style={{ padding: 12 }}>
      {/* 漫游模式控制区 */}
      <Text strong style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 8 }}>漫游模式</Text>
      <Space wrap size={6} style={{ width: '100%', marginBottom: 6 }}>
        <Tooltip title="轨道编辑：拖拽旋转、摆放模型">
          <Button
            size="small"
            icon={<EyeOutlined />}
            type={mode === 'orbit' ? 'primary' : 'default'}
            onClick={() => setMode('orbit')}
          >
            轨道编辑
          </Button>
        </Tooltip>
        <Tooltip title="WASD 第一人称漫游：点击画布锁定鼠标，ESC 退出">
          <Button
            size="small"
            icon={<UserOutlined />}
            type={mode === 'firstPerson' ? 'primary' : 'default'}
            onClick={() => setMode('firstPerson')}
          >
            第一人称
          </Button>
        </Tooltip>
        <Tooltip title="复位视角">
          <Button size="small" icon={<ReloadOutlined />} onClick={resetRoamView} />
        </Tooltip>
      </Space>

          {mode === 'firstPerson' && (
            <>
              <Space size={6} style={{ marginBottom: 6 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>飞行穿墙</Text>
                <Switch size="small" checked={isFlyMode} onChange={toggleFlyMode} />
                <Tooltip title="WASD 移动 | Shift 加速 | Ctrl 精细 | 空格上/Q下 | F 步行/飞行 | R 俯仰复位 | ESC 退出">
                  <Text style={{ fontSize: 10, color: '#666' }}><ThunderboltOutlined /> 快捷键</Text>
                </Tooltip>
              </Space>
              {/* 2026-10-16（P1-3 第一人称属性完善）：移动速度/视点高度/鼠标灵敏度（即时生效，运行时设置不外存） */}
              <div style={{ marginBottom: 6 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>移动速度 {baseSpeed.toFixed(1)}</Text>
                <Slider data-testid="roam-base-speed" min={0.5} max={20} step={0.5} value={baseSpeed} onChange={(v) => setBaseSpeed(v)} />
              </div>
              <div style={{ marginBottom: 6 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>视点高度 {eyeHeight.toFixed(1)} m</Text>
                <Slider data-testid="roam-eye-height" min={0.2} max={20} step={0.1} value={eyeHeight} onChange={(v) => setEyeHeight(v)} />
              </div>
              <div style={{ marginBottom: 6 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>鼠标灵敏度 {(mouseSensitivity * 1000).toFixed(1)}</Text>
                <Slider data-testid="roam-mouse-sensitivity" min={0.2} max={6} step={0.1} value={mouseSensitivity * 1000} onChange={(v) => setMouseSensitivity(v / 1000)} />
              </div>
            </>
          )}

      <div style={{ height: 1, background: '#30475e', margin: '10px 0' }} />

      {/* 轨道相机（路径相机）区 */}
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12, color: '#888' }}>轨道相机</Text>
        <Space size={4}>
          <Tooltip title="显示/隐藏路径线·关键帧·视锥">
            <Button size="small" icon={<AimOutlined />} type={showPathHelpers ? 'default' : 'text'}
              style={{ color: showPathHelpers ? '#f5c542' : '#888' }} onClick={toggleShowPathHelpers} />
          </Tooltip>
          <Tooltip title="添加路径相机（取当前视角为第一帧）">
            <Button size="small" type="primary" icon={<VideoCameraAddOutlined />} onClick={confirmAddPathCamera} data-testid="path-camera-add" />
          </Tooltip>
        </Space>
      </Space>

      <List
        size="small"
        dataSource={pathCameras}
        locale={{ emptyText: '暂无轨道相机，点击 + 添加' }}
        renderItem={(cam) => (
          <List.Item
            style={{ padding: '6px 0', cursor: 'pointer' }}
            onClick={() => setActivePathCamera(cam.id)}
          >
            <Space size={6} style={{ width: '100%' }}>
              <CameraOutlined style={{ color: cam.id === activePathCameraId ? '#f5c542' : '#888' }} />
              <Text style={{ fontSize: 12, color: cam.id === activePathCameraId ? '#f5c542' : '#ccc' }}>
                {cam.name}（{cam.keyFrames.length}帧）
              </Text>
              {/* 2026-10-15（P0-④ §3.5）：进入相机——编辑器视角落到该相机位姿（目标=视线方向 10m） */}
              <Tooltip title="进入相机（编辑器视角落到该相机）">
                <Button
                  type="text"
                  size="small"
                  data-testid={`path-camera-enter-${cam.id}`}
                  icon={<AimOutlined />}
                  style={{ marginLeft: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActivePathCamera(cam.id);
                    const pose = mirrorWaypointsFromCameras([cam])[0];
                    cameraControlsRegistry.applySavedCamera?.({ position: pose.position, target: pose.target });
                  }}
                />
              </Tooltip>
              <Button type="text" size="small" danger icon={<DeleteOutlined />}
                onClick={(e) => { e.stopPropagation(); removePathCamera(cam.id); setActivePathCamera(null); }} />
            </Space>
          </List.Item>
        )}
      />

      {activeCamera && (
        <div style={{ marginTop: 8 }}>
          <Space size={6} style={{ width: '100%', marginBottom: 6 }}>
            <Input size="small" style={{ flex: 1 }} value={activeCamera.name}
              onChange={(e) => updatePathCamera(activeCamera.id, { name: e.target.value })} />
            <Tooltip title="拾取当前视角添加关键帧">
              <Button size="small" icon={<PlusOutlined />} onClick={confirmAddKeyFrame} data-testid="path-camera-add-frame" />
            </Tooltip>
            <Tooltip title="循环播放">
              <Switch size="small" checked={activeCamera.loop}
                onChange={(v) => updatePathCamera(activeCamera.id, { loop: v })} />
            </Tooltip>
          </Space>
          <Space size={6} style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 11, color: '#999' }}>插值</Text>
            <Button size="small" type={activeCamera.interpolation === 'catmullRom' ? 'primary' : 'default'}
              onClick={() => updatePathCamera(activeCamera.id, { interpolation: 'catmullRom' })}>样条</Button>
            <Button size="small" type={activeCamera.interpolation === 'linear' ? 'primary' : 'default'}
              onClick={() => updatePathCamera(activeCamera.id, { interpolation: 'linear' })}>线性</Button>
          </Space>
          {/* 2026-10-16（P1 余项）：投影类型（正交=播放时无透视变形；缺省透视） */}
          <Space size={6} style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 11, color: '#999' }}>投影</Text>
            <Select
              size="small"
              data-testid="path-camera-view-type"
              style={{ width: 96 }}
              value={activeCamera.viewType ?? 'perspective'}
              onChange={(v) => updatePathCamera(activeCamera.id, { viewType: v })}
              options={[{ value: 'perspective', label: '透视' }, { value: 'orthographic', label: '正交' }]}
            />
            <Tooltip title="正交播放：视锥半高 = 注视距离 10m × tan(fov/2)（随 fov 关键帧变化）">
              <Text style={{ fontSize: 10, color: '#666' }}>正交为无透视变形</Text>
            </Tooltip>
          </Space>

          {/* 关键帧列表（选中帧可编辑时间/坐标/旋转/FOV） */}
          <List
            size="small"
            dataSource={activeCamera.keyFrames}
            locale={{ emptyText: '暂无关键帧' }}
            renderItem={(frame, i) => {
              const sel = selectedKeyFrameIndex === i;
              return (
                <List.Item style={{ padding: '6px 0' }}>
                  <Space orientation="vertical" size={2} style={{ width: '100%' }}>
                    <Space size={4}>
                      <Text style={{ fontSize: 11, color: sel ? '#f5c542' : '#999', minWidth: 30 }}>帧{i + 1}</Text>
                      <InputNumber size="small" style={{ width: 60 }} min={0} step={0.5}
                        value={frame.time} onChange={(v) => { if (v != null && Number.isFinite(v)) updateKeyFrame(activeCamera.id, i, { time: v }); }} />
                      <Text style={{ fontSize: 10, color: '#888' }}>秒</Text>
                      <Button size="small" type={sel ? 'primary' : 'default'}
                        onClick={() => setSelectedKeyFrame(sel ? null : i)}>选中</Button>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />}
                        onClick={() => { removeKeyFrame(activeCamera.id, i); setSelectedKeyFrame(null); }} />
                    </Space>
                    {sel && (
                      <Space wrap size={4} style={{ paddingLeft: 4 }}>
                        {(['position', 'rotation'] as const).map((key) =>
                          ['x', 'y', 'z'].map((axis) => {
                            const idx = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
                            // 2026-08-31｜防御：旧格式关键帧（demo-orbit-path 只有 position/target/duration）缺 rotation，
                            // 直接读 frame[key][idx] 会崩溃黑屏（点击样条帧选中时）
                            const arr = frame[key] as number[] | undefined;
                            const val = arr && Number.isFinite(arr[idx]) ? arr[idx] : 0;
                            return (
                              <InputNumber key={`${key}-${axis}`} size="small" style={{ width: 68 }}
                                value={Math.round(val * 1000) / 1000}
                                addonBefore={`${key === 'position' ? '位' : '转'}${axis.toUpperCase()}`}
                                step={key === 'position' ? 0.5 : 0.05}
                                onChange={(v) => {
                                  if (v == null || !Number.isFinite(v)) return;
                                  const cur = (frame[key] as number[] | undefined) ?? [0, 0, 0];
                                  const arr = [...cur] as number[];
                                  arr[idx] = v;
                                  updateKeyFrame(activeCamera.id, i, { [key]: arr } as any);
                                }} />
                            );
                          }),
                        )}
                        <InputNumber size="small" style={{ width: 70 }} min={20} max={120} step={1}
                          value={frame.fov} addonBefore="FOV"
                          onChange={(v) => { if (v != null && Number.isFinite(v)) updateKeyFrame(activeCamera.id, i, { fov: v }); }} />
                      </Space>
                    )}
                  </Space>
                </List.Item>
              );
            }}
          />

          {/* 2026-10-13（用户反馈 12/15/16/17）：相机实体属性面板——选中场景中相机实体（或此处）后可编辑
              位置/朝向/FOV，写回"最后一帧"；方位环（紫=左右/青=前后）在场景中直接拖拽 */}
          {activeCamera.keyFrames.length > 0 && (() => {
            const lastIdx = activeCamera.keyFrames.length - 1;
            const lastFrame = activeCamera.keyFrames[lastIdx] as any;
            const pos = (lastFrame.position as number[]) ?? [0, 0, 0];
            const rot = (lastFrame.rotation as number[]) ?? [0, 0, 0];
            const fovVal = Number.isFinite(lastFrame.fov) ? lastFrame.fov : 60;
            const num = (label: string, val: number, onVal: (v: number) => void, step: number, min?: number, max?: number, id?: string) => (
              <InputNumber size="small" style={{ width: 78 }} addonBefore={label} step={step} min={min} max={max} id={id}
                value={Math.round(val * 1000) / 1000}
                onChange={(v) => { if (v != null && Number.isFinite(v)) onVal(v); }} />
            );
            return (
              <div style={{ marginTop: 8, padding: '6px 8px', border: '1px dashed #30475e', borderRadius: 4 }}>
                <Space size={4} style={{ marginBottom: 4 }}>
                  <Text style={{ fontSize: 11, color: entitySelected ? '#ffd666' : '#888' }}>
                    相机实体属性（{entitySelected ? '已选中实体' : '点击场景中的相机实体'}）
                  </Text>
                </Space>
                <Space wrap size={4}>
                  {[0, 1, 2].map((idx) =>
                    num(`位${'XYZ'[idx]}`, pos[idx] ?? 0, (v) => {
                      const arr = [...pos]; arr[idx] = v; updateKeyFrame(activeCamera.id, lastIdx, { position: arr } as any);
                    }, 0.5, undefined, undefined, `path-camera-entity-pos-${'xyz'[idx]}`))}
                </Space>
                <Space wrap size={4} style={{ marginTop: 4 }}>
                  {[0, 1, 2].map((idx) =>
                    num(`转${'XYZ'[idx]}`, rot[idx] ?? 0, (v) => {
                      const arr = [...rot]; arr[idx] = v; updateKeyFrame(activeCamera.id, lastIdx, { rotation: arr } as any);
                    }, 0.05, undefined, undefined, `path-camera-entity-rot-${'xyz'[idx]}`))}
                  {num('FOV', fovVal, (v) => updateKeyFrame(activeCamera.id, lastIdx, { fov: v } as any), 1, 20, 120, 'path-camera-entity-fov')}
                </Space>
              </div>
            );
          })()}

          {/* 播放控制 */}
          {activeCamera.keyFrames.length >= 2 && (
            <Space orientation="vertical" style={{ width: '100%', marginTop: 8 }} size={4}>
              <Space>
                <Button size="small" type="primary" data-testid="path-camera-play" icon={<PlayCircleOutlined />}
                  disabled={isPathPlaying} onClick={() => { setMode('orbit'); setPathPlaying(true); }} />
                <Button size="small" data-testid="path-camera-pause" icon={<PauseCircleOutlined />}
                  disabled={!isPathPlaying} onClick={() => setPathPlaying(false)} />
                <Button size="small" data-testid="path-camera-stop" icon={<StopOutlined />}
                  onClick={() => setPathPlaying(false)} />
                <Text style={{ fontSize: 10, color: '#888' }}>{playTime.toFixed(1)}/{activeDuration.toFixed(1)}s</Text>
              </Space>
              <Slider min={0} max={Math.max(activeDuration, 0.1)} step={0.1}
                value={Math.min(playTime, Math.max(activeDuration, 0.1))}
                onChange={(v) => { setPlayTime(v); }} />
              <Text style={{ fontSize: 10, color: '#999' }}>速度: {cameraPathSpeed.toFixed(1)}x</Text>
              <Slider min={0.1} max={5} step={0.1} value={cameraPathSpeed} onChange={(v) => setCameraPathSpeed(v)} />
            </Space>
          )}
        </div>
      )}

      <div style={{ height: 1, background: '#30475e', margin: '12px 0' }} />

      {/* 相机路径（waypoints）：2026-10-14 起与轨道相机镜像（N 台相机 = N 视点，段数=相机数-1） */}
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12, color: '#888' }}>
          相机路径{pathCameras.length >= 2 ? `（段数=相机数-1，共 ${pathSegCount} 段）` : ''}
        </Text>
        <Tooltip title={pathCameras.length >= 2 ? '视点已按轨道相机自动镜像（位置/朝向取各相机最后一帧）' : '添加视点（独立漫游用）'}>
          <Button size="small" type="primary" icon={<PlusOutlined />}
            disabled={pathCameras.length >= 2 && waypoints.length >= pathCameras.length}
            onClick={() => {
              if (pathCameras.length >= 2 && waypoints.length >= pathCameras.length) {
                message.info('视点数与轨道相机同步（段数=相机数-1），已达上限（可先添加/删除轨道相机）');
                return;
              }
              addWaypoint({ position: [10, 10, 10], target: [0, 0, 0], duration: 3 });
            }} />
        </Tooltip>
      </Space>

      <List
        dataSource={waypoints}
        locale={{ emptyText: '暂无视点，点击 + 添加' }}
        renderItem={(wp, i) => (
          <List.Item style={{ padding: '6px 0' }}>
            <Space style={{ width: '100%' }} size="small" wrap>
              <Text style={{ fontSize: 11, color: '#999', minWidth: 20 }}>{i + 1}</Text>
              <InputNumber size="small" style={{ width: 50 }} value={wp.duration} min={0.5} max={30} step={0.5}
                onChange={(v) => { if (v != null && Number.isFinite(v)) updateWaypoint(wp.id, { duration: v }); }} />
              <Text style={{ fontSize: 10, color: '#888' }}>秒</Text>
              <Text style={{ fontSize: 10, color: '#666' }} data-testid={`waypoint-pos-${i}`}>
                位({wp.position.map((v) => v.toFixed(1)).join(', ')})
              </Text>
              {/* ≥2 台轨道相机时视点自动镜像，不允许手动删除（删了会被同步补回） */}
              {pathCameras.length < 2 && (
                <Button type="text" size="small" danger icon={<DeleteOutlined />}
                  onClick={() => removeWaypoint(wp.id)} />
              )}
            </Space>
          </List.Item>
        )}
      />

      {waypoints.length > 1 && (
        <Space style={{ marginTop: 12, width: '100%' }} orientation="vertical">
          <Space>
            <Button size="small" type="primary" icon={<PlayCircleOutlined />}
              disabled={cameraPathPlaying}
              data-testid="waypoint-play"
              onClick={() => setCameraPathPlaying(true)} />
            <Button size="small" icon={<PauseCircleOutlined />}
              disabled={!cameraPathPlaying}
              onClick={() => setCameraPathPlaying(false)} />
            <Button size="small" icon={<StopOutlined />}
              data-testid="waypoint-stop"
              onClick={() => { setCameraPathPlaying(false); }} />
          </Space>
          <div style={{ width: '100%' }}>
            <Text style={{ fontSize: 11, color: '#999' }}>速度: {cameraPathSpeed.toFixed(1)}x</Text>
            <Slider min={0.1} max={5} step={0.1} value={cameraPathSpeed}
              onChange={(v) => setCameraPathSpeed(v)} />
          </div>
        </Space>
      )}

      <div style={{ height: 1, background: '#30475e', margin: '12px 0' }} />

      {/* 第一人称自动浏览路线：编辑器选点部署（参考主流 3D 编辑器相机路径做法——
          摆好视角→加点；人视高度自动贴地），演示/发布页「自动游览」按此路线循环走动 */}
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text strong style={{ fontSize: 12, color: '#888' }}>自动浏览路线（第一人称）</Text>
        <Space size={4}>
          <Tooltip title="取当前相机位置添加浏览点（人视高度自动贴地，两点以上自动循环）">
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => {
              const cam = cameraControlsRegistry.getCamera?.();
              if (!cam) { message.info('请先在场景中摆好视角（在编辑区拖动相机）'); return; }
              addBrowsePoint([Math.round(cam.position[0] * 10) / 10, Math.round(cam.position[2] * 10) / 10]);
            }} />
          </Tooltip>
          <Tooltip title="清空全部浏览点">
            <Button size="small" icon={<DeleteOutlined />} disabled={browseRoute.length === 0}
              onClick={() => setBrowseRoute([])} />
          </Tooltip>
        </Space>
      </Space>
      {browseRoute.length === 0 ? (
        <Text style={{ fontSize: 11, color: '#666' }}>
          未部署浏览路线：在编辑区把相机移到点位（可先点「第一人称」WASD 走过去），再点 + 添加；2 个以上自动循环浏览。
        </Text>
      ) : (
        <List
          size="small"
          dataSource={browseRoute}
          locale={{ emptyText: '暂无' }}
          renderItem={(pt, i) => (
            <List.Item style={{ padding: '4px 0' }}>
              <Space size={6} style={{ width: '100%' }}>
                <Text style={{ fontSize: 11, color: '#f5c542', minWidth: 22 }}>{i + 1}</Text>
                <Text style={{ fontSize: 11, color: '#ccc' }}>X {pt[0]}</Text>
                <Text style={{ fontSize: 11, color: '#ccc' }}>Z {pt[1]}</Text>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }}
                  onClick={() => removeBrowsePoint(i)} />
              </Space>
            </List.Item>
          )}
        />
      )}
      {browseRoute.length >= 2 && (
        <Text style={{ fontSize: 11, color: '#52c41a' }}>
          已部署 {browseRoute.length} 点循环浏览 —— 保存场景后，演示/发布页点「自动游览」按此路线走动。
        </Text>
      )}
    </div>
  );
}